import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { APIProvider, Map, AdvancedMarker } from '@vis.gl/react-google-maps';
import { MapPin, CreditCard, Smartphone, Banknote, Tag, CheckCircle2, Calendar, FileText, User, Clock } from 'lucide-react';
import { useShopCart } from '../../context/ShopCartContext';
import { useShopAuth } from '../../context/ShopAuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GMAP_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const KIGALI = { lat: -1.9536, lng: 30.0606 };
const COMPANY_PURCHASE_CODE = '120291321'; // Bison Food Solutions purchase code (placeholder hint)
const SLOTS = [
  { v: '09:00', label: '09:00 – 12:00' },
  { v: '12:00', label: '12:00 – 15:00' },
  { v: '15:00', label: '15:00 – 18:00' },
];
const formatRWF = (n) => new Intl.NumberFormat('en-RW', { maximumFractionDigits: 0 }).format(n);
const tomorrowISODate = () => {
  const d = new Date(); d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};

export default function ShopCheckout() {
  const { items, subtotal, clear } = useShopCart();
  const { customer, loading, authHeader } = useShopAuth();
  const navigate = useNavigate();

  // Guest / auth mode
  const [mode, setMode] = useState('auth'); // 'auth' | 'guest'
  const [guest, setGuest] = useState({ name: '', email: '', phone: '' });

  // Delivery
  const [pinMode, setPinMode] = useState('manual');
  const [pin, setPin] = useState(KIGALI);
  const [address, setAddress] = useState({ label: 'Home', line: '', city: 'Kigali', notes: '' });

  // Scheduling
  const [scheduleMode, setScheduleMode] = useState('now'); // 'now' | 'scheduled'
  const [schedDate, setSchedDate] = useState(tomorrowISODate());
  const [schedSlot, setSchedSlot] = useState(SLOTS[0].v);

  // Payment + promo
  const [paymentMethod, setPaymentMethod] = useState('cash_on_delivery');
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(null);
  const [orderNote, setOrderNote] = useState('');

  // EBM
  const [ebmEnabled, setEbmEnabled] = useState(false);
  const [ebm, setEbm] = useState({ business_name: '', tin: '', phone: '', purchase_code: '' });

  // Quote
  const [quote, setQuote] = useState({ fee: 1500, zone_name: 'Default', distance_km: null });
  const [busy, setBusy] = useState(false);

  const isGuest = mode === 'guest' || (!customer && !loading);
  const promoAllowed = !isGuest;

  const reverseGeocode = useCallback(async (lat, lng) => {
    if (!GMAP_KEY) return;
    try {
      const r = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GMAP_KEY}`);
      const data = await r.json();
      if (data?.results?.[0]?.formatted_address) setAddress(a => ({ ...a, line: data.results[0].formatted_address }));
    } catch { /* ignore */ }
  }, []);

  // Prefill from registered customer profile
  useEffect(() => {
    if (customer) {
      setMode('auth');
      if (customer.ebm_default) setEbm(customer.ebm_default);
    } else if (!loading) {
      setMode('guest');
    }
  }, [customer, loading]);

  // Live delivery quote
  useEffect(() => {
    const scheduled = scheduleMode === 'scheduled';
    const lat = pinMode === 'map' ? pin.lat : null;
    const lng = pinMode === 'map' ? pin.lng : null;
    const params = new URLSearchParams({ scheduled: String(scheduled), subtotal: String(subtotal) });
    if (lat !== null) { params.set('lat', String(lat)); params.set('lng', String(lng)); }
    axios.get(`${API}/shop/delivery-quote?${params.toString()}`).then(r => setQuote(r.data)).catch(() => {});
  }, [subtotal, pin, pinMode, scheduleMode]);

  if (loading) return <div className="p-8 text-muted-foreground">Loading…</div>;
  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto p-12 text-center">
        <p className="text-muted-foreground mb-4">Your cart is empty.</p>
        <Button onClick={() => navigate('/shop/products')}>Continue shopping</Button>
      </div>
    );
  }

  const applyPromo = async () => {
    if (!promoAllowed) { toast.error('Promo codes are for registered customers only. Please sign in.'); return; }
    if (!promoCode) return;
    try {
      const r = await axios.post(`${API}/shop/promo/validate`, { code: promoCode.toUpperCase(), subtotal });
      setPromoApplied(r.data);
      toast.success(`Promo "${r.data.code}" applied — save RWF ${formatRWF(r.data.applied_discount)}`);
    } catch (err) {
      setPromoApplied(null);
      toast.error(err?.response?.data?.detail || 'Invalid promo code');
    }
  };

  const discount = promoApplied?.applied_discount || 0;
  const total = Math.max(0, Math.round(subtotal - discount + (quote.fee || 0)));

  const placeOrder = async () => {
    if (!address.line) { toast.error('Please enter a delivery address'); return; }
    if (isGuest && (!guest.name || !guest.email || !guest.phone)) {
      toast.error('Guest checkout requires name, email and phone'); return;
    }
    if (ebmEnabled && !ebm.business_name) { toast.error('EBM: business name is required'); return; }

    setBusy(true);
    try {
      const scheduled_for = scheduleMode === 'scheduled' ? `${schedDate}T${schedSlot}:00Z` : null;
      const ebmPayload = ebmEnabled ? ebm : null;
      const addrPayload = { ...address, lat: pinMode === 'map' ? pin.lat : null, lng: pinMode === 'map' ? pin.lng : null };
      const cartItems = items.map(i => ({ product_id: i.product_id, quantity: i.quantity }));

      let url, body, config = {};
      if (isGuest) {
        url = `${API}/shop/orders/guest`;
        body = {
          items: cartItems, address: addrPayload, payment_method: paymentMethod,
          customer_name: guest.name, customer_email: guest.email, customer_phone: guest.phone,
          customer_notes: orderNote || null, scheduled_for, ebm_invoice_request: ebmPayload,
        };
      } else {
        url = `${API}/shop/orders`;
        body = {
          items: cartItems, address: addrPayload, payment_method: paymentMethod,
          promo_code: promoApplied?.code || null, customer_notes: orderNote || null,
          scheduled_for, ebm_invoice_request: ebmPayload,
        };
        config = { headers: authHeader() };
      }
      const r = await axios.post(url, body, config);
      clear();
      toast.success(isGuest ? 'Order placed as guest!' : 'Order placed!');
      if (isGuest) navigate('/shop'); else navigate(`/shop/orders/${r.data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not place order');
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-serif font-bold mb-6">Checkout</h1>
      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-6">

          {/* Account / Guest */}
          {!customer && (
            <section className="p-5 bg-card rounded-xl border border-border" data-testid="checkout-mode-section">
              <h2 className="font-semibold mb-3 flex items-center gap-2"><User className="w-4 h-4 text-primary" />Account</h2>
              <RadioGroup value={mode} onValueChange={setMode} className="flex gap-4 mb-3">
                <label className="flex items-center gap-2 cursor-pointer text-sm"><RadioGroupItem value="guest" data-testid="mode-guest" />Continue as guest</label>
                <label className="flex items-center gap-2 cursor-pointer text-sm"><RadioGroupItem value="auth" data-testid="mode-signin" />Sign in / register</label>
              </RadioGroup>
              {mode === 'auth' ? (
                <div className="text-sm text-muted-foreground">
                  <Button variant="link" className="p-0 h-auto" onClick={() => navigate('/shop/login')} data-testid="checkout-go-signin">Sign in</Button> or{' '}
                  <Button variant="link" className="p-0 h-auto" onClick={() => navigate('/shop/register')} data-testid="checkout-go-register">create an account</Button> to use promo codes & rewards.
                </div>
              ) : (
                <div className="grid sm:grid-cols-3 gap-3">
                  <div><Label>Full name *</Label><Input value={guest.name} onChange={(e) => setGuest(g => ({...g, name: e.target.value}))} data-testid="guest-name" /></div>
                  <div><Label>Email *</Label><Input type="email" value={guest.email} onChange={(e) => setGuest(g => ({...g, email: e.target.value}))} data-testid="guest-email" /></div>
                  <div><Label>Phone *</Label><Input value={guest.phone} onChange={(e) => setGuest(g => ({...g, phone: e.target.value}))} data-testid="guest-phone" /></div>
                  <div className="sm:col-span-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">Promo codes, rewards & order history are only available to registered customers.</div>
                </div>
              )}
            </section>
          )}

          {/* Delivery */}
          <section className="p-5 bg-card rounded-xl border border-border">
            <h2 className="font-semibold mb-3 flex items-center gap-2"><MapPin className="w-4 h-4 text-primary" />Delivery address</h2>
            <RadioGroup value={pinMode} onValueChange={setPinMode} className="flex gap-4 mb-4" data-testid="pin-mode-group">
              <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="manual" data-testid="pin-mode-manual" />Type manually</label>
              <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="map" data-testid="pin-mode-map" />Drop pin on map</label>
            </RadioGroup>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Label</Label><Input value={address.label} onChange={(e) => setAddress(a => ({...a, label: e.target.value}))} data-testid="addr-label" /></div>
              <div><Label>City</Label><Input value={address.city} onChange={(e) => setAddress(a => ({...a, city: e.target.value}))} data-testid="addr-city" /></div>
              <div className="sm:col-span-2"><Label>Address {pinMode === 'map' ? '(auto-filled from map pin)' : ''}</Label><Input value={address.line} onChange={(e) => setAddress(a => ({...a, line: e.target.value}))} placeholder="Street, building, neighbourhood..." data-testid="addr-line" /></div>
              <div className="sm:col-span-2"><Label>Delivery notes (optional)</Label><Textarea rows={2} value={address.notes} onChange={(e) => setAddress(a => ({...a, notes: e.target.value}))} placeholder="Gate code, landmarks..." data-testid="addr-notes" /></div>
            </div>
            {pinMode === 'map' && (
              <div className="mt-4 rounded-lg overflow-hidden border border-border" style={{ height: 320 }}>
                {GMAP_KEY ? (
                  <APIProvider apiKey={GMAP_KEY}>
                    <Map defaultCenter={pin} defaultZoom={13} mapId="bison-shop-map" gestureHandling="greedy" disableDefaultUI={false}
                      onClick={(e) => { const ll = e.detail.latLng; setPin({ lat: ll.lat, lng: ll.lng }); reverseGeocode(ll.lat, ll.lng); }}>
                      <AdvancedMarker position={pin} />
                    </Map>
                  </APIProvider>
                ) : <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Map unavailable.</div>}
              </div>
            )}
          </section>

          {/* Scheduling */}
          <section className="p-5 bg-card rounded-xl border border-border" data-testid="schedule-section">
            <h2 className="font-semibold mb-3 flex items-center gap-2"><Clock className="w-4 h-4 text-primary" />Delivery time</h2>
            <RadioGroup value={scheduleMode} onValueChange={setScheduleMode} className="space-y-2">
              <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition ${scheduleMode === 'now' ? 'border-primary bg-primary/5' : 'border-border'}`}>
                <RadioGroupItem value="now" data-testid="sched-now" />
                <div className="flex-1"><div className="font-medium">Deliver now</div><div className="text-xs text-muted-foreground">Same-day delivery, fastest option.</div></div>
              </label>
              <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition ${scheduleMode === 'scheduled' ? 'border-primary bg-primary/5' : 'border-border'}`}>
                <RadioGroupItem value="scheduled" data-testid="sched-scheduled" />
                <div className="flex-1">
                  <div className="font-medium flex items-center gap-2"><Calendar className="w-4 h-4" />Schedule for later</div>
                  <div className="text-xs text-muted-foreground mb-2">Pick a date and time window. Scheduled delivery has a slightly higher fee.</div>
                  {scheduleMode === 'scheduled' && (
                    <div className="grid grid-cols-2 gap-2 mt-1" onClick={(e) => e.stopPropagation()}>
                      <Input type="date" min={tomorrowISODate()} value={schedDate} onChange={(e) => setSchedDate(e.target.value)} data-testid="sched-date" />
                      <Select value={schedSlot} onValueChange={setSchedSlot}>
                        <SelectTrigger data-testid="sched-slot"><SelectValue /></SelectTrigger>
                        <SelectContent>{SLOTS.map(s => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </label>
            </RadioGroup>
          </section>

          {/* Payment */}
          <section className="p-5 bg-card rounded-xl border border-border">
            <h2 className="font-semibold mb-3">Payment method</h2>
            <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="space-y-2" data-testid="payment-method-group">
              {[
                { v: 'cash_on_delivery', label: 'Cash on Delivery', icon: Banknote, note: 'Pay when your order arrives.' },
                { v: 'mobile_money', label: 'Mobile Money (MTN MoMo / Airtel)', icon: Smartphone, note: 'You will receive a payment prompt after we confirm your order.' },
                { v: 'card', label: 'Credit / Debit Card', icon: CreditCard, note: 'Secure card payment processed after order confirmation.' },
              ].map(opt => (
                <label key={opt.v} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition ${paymentMethod === opt.v ? 'border-primary bg-primary/5' : 'border-border'}`}>
                  <RadioGroupItem value={opt.v} data-testid={`payment-${opt.v}`} />
                  <opt.icon className="w-5 h-5 text-primary mt-0.5" />
                  <div className="flex-1"><div className="font-medium">{opt.label}</div><div className="text-xs text-muted-foreground">{opt.note}</div></div>
                </label>
              ))}
            </RadioGroup>
          </section>

          {/* Order notes */}
          <section className="p-5 bg-card rounded-xl border border-border">
            <h2 className="font-semibold mb-2">Order notes (optional)</h2>
            <p className="text-xs text-muted-foreground mb-2">Cashier, rider, supervisor and manager will all see this note with the new order alert.</p>
            <Textarea rows={3} value={orderNote} onChange={(e) => setOrderNote(e.target.value)} placeholder="e.g. Pack tomatoes separately, no plastic bags…" data-testid="order-note" />
          </section>

          {/* EBM invoice */}
          <section className="p-5 bg-card rounded-xl border border-border" data-testid="ebm-section">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-semibold flex items-center gap-2"><FileText className="w-4 h-4 text-primary" />Request EBM invoice</h2>
              <Switch checked={ebmEnabled} onCheckedChange={setEbmEnabled} data-testid="ebm-toggle" />
            </div>
            {ebmEnabled && (
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                <div><Label>Business name *</Label><Input value={ebm.business_name} onChange={(e) => setEbm(s => ({...s, business_name: e.target.value}))} data-testid="ebm-business-name" /></div>
                <div><Label>TIN</Label><Input value={ebm.tin} onChange={(e) => setEbm(s => ({...s, tin: e.target.value}))} placeholder="Your business TIN" data-testid="ebm-tin" /></div>
                <div><Label>Phone</Label><Input value={ebm.phone} onChange={(e) => setEbm(s => ({...s, phone: e.target.value}))} data-testid="ebm-phone" /></div>
                <div><Label>Purchase code (where applicable)</Label><Input value={ebm.purchase_code} onChange={(e) => setEbm(s => ({...s, purchase_code: e.target.value}))} placeholder={COMPANY_PURCHASE_CODE} data-testid="ebm-purchase-code" /></div>
                <div className="sm:col-span-2 text-xs text-muted-foreground">We will upload the EBM invoice once your order is processed. You'll be notified to download it.</div>
              </div>
            )}
          </section>
        </div>

        {/* Summary */}
        <div>
          <div className="p-5 bg-card rounded-xl border border-border sticky top-24">
            <div className="font-semibold mb-3">Order summary</div>
            <div className="space-y-2 mb-3 max-h-48 overflow-auto" data-testid="checkout-items-list">
              {items.map(i => (
                <div key={i.product_id} className="flex justify-between text-sm">
                  <span className="truncate pr-2">{i.name} <span className="text-muted-foreground">× {i.quantity}</span></span>
                  <span>RWF {formatRWF(i.unit_price * i.quantity)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-border pt-3 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span data-testid="summary-subtotal">RWF {formatRWF(subtotal)}</span></div>
              {discount > 0 && <div className="flex justify-between text-green-700"><span>Promo {promoApplied.code}</span><span>-RWF {formatRWF(discount)}</span></div>}
              <div className="flex justify-between" data-testid="summary-delivery"><span className="text-muted-foreground">Delivery · {quote.zone_name}{quote.distance_km !== null ? ` · ${quote.distance_km} km` : ''}</span><span>{quote.fee === 0 ? 'Free' : `RWF ${formatRWF(quote.fee)}`}</span></div>
              <div className="flex justify-between font-bold text-base pt-2 border-t border-border mt-2"><span>Total</span><span data-testid="summary-total">RWF {formatRWF(total)}</span></div>
            </div>
            {/* Promo input */}
            <div className="mt-3 flex gap-2">
              <Input placeholder={promoAllowed ? 'Promo code' : 'Sign in for promo codes'} value={promoCode} onChange={(e) => setPromoCode(e.target.value)} disabled={!promoAllowed} data-testid="promo-input" />
              <Button variant="outline" size="sm" onClick={applyPromo} type="button" disabled={!promoAllowed} data-testid="promo-apply"><Tag className="w-4 h-4" /></Button>
            </div>
            {promoApplied && <div className="text-xs text-green-700 mt-1 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {promoApplied.code} applied</div>}
            <Button className="w-full mt-4" size="lg" onClick={placeOrder} disabled={busy} data-testid="place-order-btn">
              {busy ? 'Placing order…' : `Place order (RWF ${formatRWF(total)})`}
            </Button>
            <p className="text-xs text-muted-foreground mt-2 text-center">{isGuest ? 'You will receive order updates by email.' : 'You will receive an order confirmation.'}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
